import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useHash } from 'hooks/use-hash';
import { useLocalStorage } from 'hooks/use-local-storage';
import { ValidViewPresets } from './scheduler-config/view-presets/view-presets';
import { viewPresetTransformer } from './transformers';
import { ResourceManagmentPageParams } from './types';

interface Props {
  loading: boolean;
  key: string;
  pageParams: ResourceManagmentPageParams;
  toUrlFormat: (params: ResourceManagmentPageParams) => string;
  fromUrlFormat: (query: string) => ResourceManagmentPageParams;
  replacePageParams: (params: ResourceManagmentPageParams) => void;
}
export const useLocalStorageState = ({
  loading,
  key,
  toUrlFormat,
  fromUrlFormat,
  replacePageParams,
  pageParams,
}: Props): boolean => {
  const [initialized, setInitialized] = useState(false);
  const [query, storeQuery] = useLocalStorage(key, '');
  const [_, _ignore, getNextViewPreset] = useHash(
    viewPresetTransformer(ValidViewPresets),
  );
  const [zoomHash, storeZoomHash] = useLocalStorage(
    'resource_management.zoom_level',
    '',
  );
  const { search: searchParams = '', hash: hashParams = '' } = useLocation();

  useEffect(() => {
    if (!initialized || loading) {
      return;
    }

    storeQuery(toUrlFormat(pageParams));
    storeZoomHash(getNextViewPreset(pageParams.viewPreset));
  }, [
    getNextViewPreset,
    initialized,
    loading,
    pageParams,
    storeQuery,
    storeZoomHash,
    toUrlFormat,
  ]);

  useEffect(() => {
    if (initialized || loading) {
      return;
    }
    const restoreQuery =
      searchParams === '' ? query.split('#')?.[0] ?? '' : searchParams;
    const restoreHashparam = hashParams === '' ? zoomHash : hashParams;
    const savedParams = fromUrlFormat(restoreQuery + restoreHashparam);

    replacePageParams(savedParams);
    setInitialized(true);
  }, [
    fromUrlFormat,
    hashParams,
    initialized,
    loading,
    query,
    replacePageParams,
    searchParams,
    zoomHash,
  ]);

  return initialized;
};
